import React, {useState, useMemo, useEffect} from 'react';
import { useDispatch, useSelector } from "react-redux";
import styles from './Queue.module.scss';
import {Col, Container, Row, Card, Button, OverlayTrigger, Tooltip} from "react-bootstrap";
import ContentLoader from "react-content-loader";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFire} from "@fortawesome/free-solid-svg-icons";
import {SearchBar} from "../../components";
import {NotificationPopup} from "../../components/NotificationPopup/NotificationPopup";
import {
    fetchProjects,
    selectProjectById,
    selectProjectIds,
} from "../projects/projectsSlice";
import { fetchSideInfo } from "../mediums/mediumsSlice";
import { Link } from "react-router-dom";



const QueueCard = ({ queue, sideInfo }) => {
    // Access user authentication state for role-based access control
    const user = useSelector(state => state.authentication.user);

    // Determine if user can moderate (same logic as in Attendance.js and SingleMediaBox.js)
    const canUserModerate = user && !user.is_admin_or_qa;

    const renderPreviewTooltip = (props) => (
        <Tooltip id="preview-tooltip" {...props}>
            View queue content in preview mode (read-only access)
        </Tooltip>
    );

    return (
        <Col lg={4} md={6} className="mb-4">
            <Card className={styles['queue-card']}>
                <Card.Header className={styles['queue-header']}>
                    <div className={styles['queue-title']}>
                        <FontAwesomeIcon icon={faFire} className={styles['queue-icon']} />
                        <h5 className="mb-0">{queue.name}</h5>
                    </div>
                </Card.Header>
                <Card.Body className={styles['queue-body']}>
                    <div className={styles['queue-stats']}>
                        <div className={styles['stat-item']}>
                            <span className={styles['stat-label']}>Open task: {sideInfo && sideInfo.requests_downloaded || 0}</span>
                            {/* <span className={styles['stat-value']}></span> */}
                        </div>
                        <div className={styles['stat-item']}>
                            <span className={styles['stat-label']}>My task:</span>
                            <span className={styles['stat-value']}>{queue.myTask}</span>
                        </div>
                    </div>
                    
                    <div className={styles['queue-actions']}>
                        {canUserModerate && (
                            <Link
                                to={`/projects/${queue.id}`}
                                style={{ textDecoration: "none" }}
                            >
                                <Button variant="link" className={styles['moderate-btn']}>
                                    Moderate
                                </Button>
                            </Link>
                        )}
                        <Link
                            to={`/projects/${queue.id}?preview=true`}
                            style={{ textDecoration: "none" }}
                        >
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 250, hide: 400 }}
                                overlay={renderPreviewTooltip}
                            >
                                <Button variant="link" className={styles['preview-btn']}>
                                    Preview
                                </Button>
                            </OverlayTrigger>
                        </Link>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
};

export function QueuesList() {
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading] = useState(false);
    const [sideInfoData, setSideInfoData] = useState({});
    const dispatch = useDispatch();
    const orderedProjectIds = useSelector(selectProjectIds);
    const projectStatus = useSelector((state) => state.projects.status);
    
    useEffect(() => {
        if (projectStatus === "idle") {
            dispatch(fetchProjects());
        }
    }, [projectStatus, dispatch]);
    
    const projects = useSelector((state) => orderedProjectIds.map(id => selectProjectById(state, id)));
    
    // Fetch sideInfo for each project when projects are loaded
    useEffect(() => {
        if (projects.length > 0) {
            projects.forEach(project => {
                if (!sideInfoData[project.id]) {
                    dispatch(fetchSideInfo({ project_id: project.id }))
                        .then((result) => {
                            if (result.payload && result.payload.info) {
                                setSideInfoData(prev => ({
                                    ...prev,
                                    [project.id]: result.payload.info
                                }));
                            }
                        })
                        .catch((error) => {
                            console.error(`Failed to fetch sideInfo for project ${project.id}:`, error);
                        });
                }
            });
        }
    }, [projects, dispatch, sideInfoData]);

    const filteredQueues = useMemo(() => {
        if (!searchTerm.trim()) {
            return [...projects].sort((a, b) => a.id - b.id);
        }
        const searchLower = searchTerm.toLowerCase();
        return projects
            .filter(queue =>
                queue.name.toLowerCase().includes(searchLower) ||
                queue.id.toString().includes(searchTerm)
            )
            .sort((a, b) => a.id - b.id);
    }, [searchTerm, projects]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const LoadingCard = () => (
        <Col lg={4} md={6} className="mb-4">
            <ContentLoader
                speed={2}
                width="100%"
                height={200}
                viewBox="0 0 100 200"
                backgroundColor="#242526"
                foregroundColor="#3a3b3c"
                className={styles['loading-card']}
            >
                <rect x="0" y="0" rx="8" ry="8" width="100" height="200" />
            </ContentLoader>
        </Col>
    );

    let content;

    if (isLoading) {
        content = (
            <Row>
                {[...Array(6)].map((_, index) => (
                    <LoadingCard key={index} />
                ))}
            </Row>
        );
    } else if (filteredQueues.length === 0 && searchTerm.trim()) {
        content = (
            <Row>
                <Col>
                    <div className={styles['no-results']}>
                        <div className={styles['no-results-content']}>
                            <FontAwesomeIcon icon={faFire} className={styles['no-results-icon']} />
                            <h4>No queues found</h4>
                            <p>No queues match your search for "{searchTerm}". Try searching with a different queue number or name.</p>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    } else {
        content = (
            <Row>
                {filteredQueues.map((queue) => (
                    <QueueCard 
                        key={queue.id} 
                        queue={queue} 
                        sideInfo={sideInfoData[queue.id]} 
                    />
                ))}
            </Row>
        );
    }

    return (
        <section className={styles['queues-list']}>
            <NotificationPopup />
            <div className={styles['main-content-area']}>
                <SearchBar
                    onSearch={handleSearch}
                    placeholder="Search by queue number or name..."
                />
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col xs={12}>
                            {content}
                        </Col>
                    </Row>
                </Container>
            </div>
        </section>
    );
}
